import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { APP_CONSTANTS } from 'src/consumer/constants'
import { usePatient } from '../context/PatientContext'
import { getUnreadCount } from '../services/notificationsService'
import PatientProfileSwitcher from './PatientProfileSwitcher'
import PatientPortalSwitcher from './PatientPortalSwitcher'

// Two-letter brand initials for the logo fallback (when the brand has no
// uploaded image, or the upload fails to load). "Sunrise Wellness" → "SW",
// "Greenleaf Clinic" → "GC", single-word "Apollo" → "AP".
function brandInitials(name) {
  if (!name) return '·'
  const words = String(name).trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return '·'
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return (words[0][0] + words[1][0]).toUpperCase()
}

const Bar = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: ${(p) => p.theme?.layout?.topBarHeight || '64px'};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  background: ${(p) => p.theme?.color?.bgRaised || '#fff'};
  border-bottom: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  z-index: ${(p) => p.theme?.z?.topBar || 30};
  @media (min-width: 768px) {
    padding: 0 24px;
  }
`

// Persistent AUMHUM mark — sits left of the hamburger so the product
// identity is always visible alongside the per-profile clinic brand. Same
// APP_CONSTANTS.APP_LOGO_LINK + /logo_brand.png fallback every other portal
// (mentor / business / kiosk / consumer signin) uses.
const AumhumMark = styled.button`
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  transition: background 200ms ease-out;
  &:hover {
    background: ${(p) => p.theme?.color?.bgSubtle || '#F4F0E9'};
  }
`

const AumhumMarkImg = styled.img`
  width: 28px;
  height: 28px;
  object-fit: contain;
`

const Brand = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 16px;
  font-weight: 700;
  color: ${(p) => p.theme?.color?.primary || '#1F3A5F'};
  letter-spacing: -0.01em;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 8px;
  border-radius: 8px;
  max-width: 240px;
  min-width: 0;
  &:hover {
    background: ${(p) => p.theme?.color?.bgSubtle || '#F4F0E9'};
  }
`

const BrandLogo = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
  background: ${(p) => p.theme?.color?.bgSubtle || '#F4F0E9'};
`

const BrandLogoFallback = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: ${(p) => p.theme?.color?.primarySoft || '#E8EEF6'};
  color: ${(p) => p.theme?.color?.primary || '#1F3A5F'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  flex-shrink: 0;
`

const BrandText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const IconButton = styled.button`
  appearance: none;
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  background: ${(p) => p.theme?.color?.bgRaised || '#fff'};
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  color: ${(p) => p.theme?.color?.ink || '#1F2937'};
  position: relative;
  transition: background 200ms ease-out;
  &:hover {
    background: ${(p) => p.theme?.color?.bgSubtle || '#F4F0E9'};
  }
`

const Badge = styled.span`
  position: absolute;
  top: 4px;
  right: 4px;
  min-width: 16px;
  height: 16px;
  border-radius: 999px;
  background: ${(p) => p.theme?.color?.urgent || '#C24D49'};
  color: white;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
`

export default function PatientTopBar({ onMenuClick }) {
  const navigate = useNavigate()
  const { currentProfile, currentProfileId } = usePatient()
  const [unread, setUnread] = useState(0)
  // Track logo load failures locally so a 404'd img URL falls back to the
  // initials chip rather than rendering a broken-image glyph.
  const [logoBroken, setLogoBroken] = useState(false)
  useEffect(() => {
    setLogoBroken(false)
  }, [currentProfile?.brandLogoUrl])

  // Refresh the bell-icon badge whenever the active profile changes
  // (each profile has its own notification stream — switching profiles
  // means re-asking the server for that patient's unread count). Doesn't
  // poll on a timer; new notifications arrive via push and the count
  // refreshes on the next route navigation that re-mounts this bar.
  useEffect(() => {
    let cancelled = false
    if (!currentProfileId) {
      setUnread(0)
      return () => {}
    }
    getUnreadCount()
      .then((res) => {
        if (cancelled) return
        const n = typeof res?.count === 'number' ? res.count : 0
        setUnread(n)
      })
      .catch(() => {
        if (!cancelled) setUnread(0)
      })
    return () => {
      cancelled = true
    }
  }, [currentProfileId])

  return (
    <Bar>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <AumhumMark
          aria-label="AUMHUM"
          onClick={() => navigate('/patient-home')}
        >
          <AumhumMarkImg
            src={APP_CONSTANTS.APP_LOGO_LINK}
            alt="AUMHUM"
            onError={(e) => {
              e.target.src = '/logo_brand.png'
            }}
          />
        </AumhumMark>
        <IconButton
          aria-label="Menu"
          onClick={onMenuClick}
          style={{ display: 'flex' }}
          className="patient-topbar-menu-btn"
        >
          ☰
        </IconButton>
        <Brand
          onClick={() => navigate('/patient-home')}
          aria-label={currentProfile?.brandName || 'Home'}
        >
          {currentProfile?.brandLogoUrl && !logoBroken ? (
            <BrandLogo
              src={currentProfile.brandLogoUrl}
              alt=""
              onError={() => setLogoBroken(true)}
            />
          ) : (
            <BrandLogoFallback aria-hidden>
              {brandInitials(currentProfile?.brandName)}
            </BrandLogoFallback>
          )}
          <BrandText>{currentProfile?.brandName || 'Home'}</BrandText>
        </Brand>
      </div>
      <Right>
        <PatientPortalSwitcher />
        <IconButton
          aria-label="Notifications"
          onClick={() => navigate('/patient-home/notifications')}
        >
          🔔
          {unread > 0 ? <Badge>{unread > 99 ? '99+' : unread}</Badge> : null}
        </IconButton>
        <IconButton aria-label="Messages" onClick={() => navigate('/patient-home/messages')}>
          💬
        </IconButton>
        <PatientProfileSwitcher />
      </Right>
    </Bar>
  )
}
